import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { TextField, InputAdornment, SxProps } from '@mui/material';
import { getTablePreferences, setTablePreferences } from 'components/utils';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

const SearchInput = ({
  tableName,
  pageParamName,
  onChange,
  value: valueProp,
  sx,
}: {
  tableName: string;
  pageParamName: string;
  onChange: (search: string) => void;
  value?: string;
  sx?: SxProps;
}) => {
  const router = useRouter();
  const initialSearch = valueProp ?? (getTablePreferences(tableName).search as string) ?? '';

  const [value, setValue] = useState(initialSearch);

  const [showClearIcon, setShowClearIcon] = useState(initialSearch ? 'flex' : 'none');

  useEffect(() => {
    if (valueProp !== undefined) {
      setValue(valueProp);
      setShowClearIcon(valueProp ? 'flex' : 'none');
    }
  }, [valueProp]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  const resetUrlParam = () => {
    const query = { ...router.query };

    if (query[pageParamName]) {
      query[pageParamName] = '';
    }

    if (query[`${tableName}Page`]) {
      query[`${tableName}Page`] = '';
    }

    // Only run router.push if something changed
    if (JSON.stringify(router.query) !== JSON.stringify(query)) {
      void router.push({
        query,
      });
    }
  };

  const handleClear = () => {
    setValue('');
    setShowClearIcon('none');

    setTablePreferences({
      tableName,
      values: [
        {
          key: 'search',
          value: '',
        },
      ],
    });

    resetUrlParam();

    onChange('');
  };

  const handleSearchChange = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const value = (e.target as HTMLInputElement).value;

      setShowClearIcon(value === '' ? 'none' : 'flex');

      setTablePreferences({
        tableName,
        values: [
          {
            key: 'search',
            value,
          },
        ],
      });

      resetUrlParam();

      onChange(value);
    }
  };

  return (
    <TextField
      size="small"
      variant="outlined"
      onChange={handleChange}
      value={value}
      onKeyDown={handleSearchChange}
      sx={{
        width: {
          xs: '100%',
          sm: '240px',
          md: '310px',
          lg: '310px',
          xl: '350px',
        },
        marginBottom: {
          xs: 1,
          sm: 0,
        },
        ...(sx || {}),
      }}
      placeholder="Search ..."
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end" style={{ display: showClearIcon }}>
            <ClearIcon onClick={handleClear} sx={{ cursor: 'pointer' }} />
          </InputAdornment>
        ),
      }}
    />
  );
};

export default SearchInput;
